/**
 * Dear Diary Theme - Decorative Elements
 * Phase 3: Paper clips, washi tape, coffee stains, stamps, and more
 */

import React from 'react'
import { useTheme } from '@/components/providers/ThemeProvider'

// ============================================
// PAPER CLIP
// ============================================
interface PaperClipProps {
  color?: 'silver' | 'gold' | 'rose' | 'black'
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  className?: string
}

export const PaperClip: React.FC<PaperClipProps> = ({ 
  color = 'silver', 
  position = 'top-right',
  className = ''
}) => {
  const { isDiary } = useTheme()
  if (!isDiary) return null

  const colors = {
    silver: { main: '#A8A8A8', shadow: '#888888' },
    gold: { main: 'var(--accent-gold)', shadow: 'var(--accent-gold)' },
    rose: { main: '#E8B4B8', shadow: '#C8949A' },
    black: { main: '#333333', shadow: '#111111' },
  }

  const positions = {
    'top-left': { top: '-10px', left: '15px', transform: 'rotate(-15deg)' },
    'top-right': { top: '-10px', right: '15px', transform: 'rotate(15deg)' },
    'bottom-left': { bottom: '-10px', left: '15px', transform: 'rotate(-15deg)' },
    'bottom-right': { bottom: '-10px', right: '15px', transform: 'rotate(15deg)' },
  }

  const c = colors[color]
  const pos = positions[position]

  return (
    <svg
      width="20"
      height="50"
      viewBox="0 0 20 50"
      className={`absolute z-10 ${className}`}
      style={pos}
    >
      <path
        d="M10,2 C14,2 17,5 17,10 L17,38 C17,43 14,46 10,46 C6,46 3,43 3,38 L3,15 C3,12 5,10 8,10 L8,35"
        fill="none"
        stroke={c.main}
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <path
        d="M10,2 C14,2 17,5 17,10 L17,38 C17,43 14,46 10,46 C6,46 3,43 3,38 L3,15 C3,12 5,10 8,10 L8,35"
        fill="none"
        stroke={c.shadow}
        strokeWidth="2.5"
        strokeLinecap="round"
        opacity="0.3"
        transform="translate(1,1)"
      />
    </svg>
  )
}

// ============================================
// WASHI TAPE
// ============================================
interface WashiTapeProps {
  color?: 'pink' | 'blue' | 'yellow' | 'green' | 'purple'
  width?: number
  rotation?: number
  position?: { top?: string; left?: string; right?: string; bottom?: string }
  pattern?: 'stripes' | 'dots' | 'plain'
  className?: string
}

export const WashiTape: React.FC<WashiTapeProps> = ({
  color = 'pink',
  width = 80,
  rotation = -3,
  position = { top: '-12px', left: '20px' },
  pattern = 'stripes',
  className = ''
}) => {
  const { isDiary } = useTheme()
  if (!isDiary) return null

  const colors = {
    pink: 'var(--washi-tape-pink)',
    blue: 'var(--washi-tape-blue)',
    yellow: 'var(--highlight-yellow)',
    green: 'var(--washi-tape-green)',
    purple: '#E8E1F8',
  }

  const patterns = {
    stripes: `repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(255,255,255,0.5) 4px, rgba(255,255,255,0.5) 8px)`,
    dots: `radial-gradient(circle, rgba(255,255,255,0.5) 2px, transparent 2px)`,
    plain: 'none',
  }

  return (
    <div
      className={`absolute h-6 ${className}`}
      style={{
        ...position,
        width: `${width}px`,
        background: colors[color],
        backgroundImage: patterns[pattern],
        backgroundSize: pattern === 'dots' ? '10px 10px' : undefined,
        transform: `rotate(${rotation}deg)`,
        boxShadow: '1px 1px 3px rgba(0,0,0,0.1)',
        opacity: 0.85,
      }}
    />
  )
}

// ============================================
// COFFEE STAIN
// ============================================
interface CoffeeStainProps {
  size?: 'small' | 'medium' | 'large'
  opacity?: number
  position?: { top?: string; left?: string; right?: string; bottom?: string }
  className?: string
}

export const CoffeeStain: React.FC<CoffeeStainProps> = ({
  size = 'medium',
  opacity = 0.08,
  position = { bottom: '20px', right: '20px' },
  className = ''
}) => {
  const { isDiary } = useTheme()
  if (!isDiary) return null

  const sizes = { small: 50, medium: 80, large: 120 }
  const s = sizes[size]

  return (
    <svg
      width={s}
      height={s}
      viewBox="0 0 100 100"
      className={`absolute pointer-events-none ${className}`}
      style={{ ...position, opacity }}
    >
      <ellipse cx="50" cy="50" rx="45" ry="42" fill="#8B4513" opacity="0.3" />
      <ellipse cx="50" cy="50" rx="42" ry="39" fill="none" stroke="#8B4513" strokeWidth="3" opacity="0.5" />
      <ellipse cx="48" cy="52" rx="35" ry="32" fill="none" stroke="#8B4513" strokeWidth="2" opacity="0.3" />
      {/* Coffee drops */}
      <circle cx="85" cy="65" r="8" fill="#8B4513" opacity="0.2" />
      <circle cx="20" cy="80" r="5" fill="#8B4513" opacity="0.15" />
    </svg>
  )
}

// ============================================
// STAMP
// ============================================
interface StampProps {
  text: string
  color?: 'purple' | 'red' | 'blue' | 'green'
  rotation?: number
  className?: string
}

export const Stamp: React.FC<StampProps> = ({
  text,
  color = 'purple',
  rotation = -5,
  className = ''
}) => {
  const { isDiary } = useTheme()
  if (!isDiary) return null

  const colors = {
    purple: 'var(--horton-purple)',
    red: '#C45C4A',
    blue: '#1E3A5F',
    green: '#4A7C59',
  }

  return (
    <div
      className={`inline-flex items-center justify-center px-4 py-2 diary-stamp-appear ${className}`}
      style={{
        border: `3px solid ${colors[color]}`,
        borderRadius: '4px',
        color: colors[color],
        fontFamily: "'Courier New', monospace",
        fontWeight: 'bold',
        textTransform: 'uppercase',
        letterSpacing: '2px',
        fontSize: '12px',
        transform: `rotate(${rotation}deg)`,
        opacity: 0.85,
      }}
    >
      {text}
    </div>
  )
}

// ============================================
// BOOKMARK RIBBON
// ============================================
interface BookmarkRibbonProps {
  color?: 'red' | 'blue' | 'gold' | 'green'
  position?: 'left' | 'right'
  length?: number
  className?: string
}

export const BookmarkRibbon: React.FC<BookmarkRibbonProps> = ({
  color = 'red',
  position = 'right',
  length = 80,
  className = ''
}) => {
  const { isDiary } = useTheme()
  if (!isDiary) return null

  const colors = {
    red: { main: '#C45C4A', dark: '#9A3A2A' },
    blue: { main: '#4A90A4', dark: '#3A7084' },
    gold: { main: 'var(--accent-gold)', dark: 'var(--accent-gold)' },
    green: { main: '#4A7C59', dark: '#3A5C49' },
  }

  const c = colors[color]
  const posStyle = position === 'right' ? { right: '25px' } : { left: '25px' }

  return (
    <div
      className={`absolute top-0 ${className}`}
      style={{
        ...posStyle,
        width: '4px',
        height: `${length}px`,
        background: `linear-gradient(to bottom, ${c.main}, ${c.dark})`,
        borderRadius: '0 0 2px 2px',
        boxShadow: '1px 1px 3px rgba(0,0,0,0.2)',
      }}
    >
      {/* Ribbon tail */}
      <div
        style={{
          position: 'absolute',
          bottom: '-12px',
          left: '-4px',
          width: 0,
          height: 0,
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
          borderTop: `14px solid ${c.dark}`,
        }}
      />
    </div>
  )
}

// ============================================
// DOODLE STARS
// ============================================
interface DoodleStarsProps {
  count?: number
  spread?: 'tight' | 'wide'
  className?: string
}

export const DoodleStars: React.FC<DoodleStarsProps> = ({
  count = 3,
  spread = 'tight',
  className = ''
}) => {
  const { isDiary } = useTheme()
  if (!isDiary) return null

  const stars = Array.from({ length: count }, (_, i) => {
    const spreadFactor = spread === 'wide' ? 30 : 15
    return {
      top: `${Math.random() * spreadFactor}px`,
      left: `${i * (spread === 'wide' ? 25 : 15)}px`,
      rotation: Math.random() * 30 - 15,
      size: 12 + Math.random() * 6,
    }
  })

  return (
    <div className={`relative inline-flex ${className}`} style={{ width: count * 20 }}>
      {stars.map((star, i) => (
        <span
          key={i}
          className="absolute"
          style={{
            top: star.top,
            left: star.left,
            transform: `rotate(${star.rotation}deg)`,
            fontSize: `${star.size}px`,
            color: 'var(--horton-purple)',
            opacity: 0.7,
          }}
        >
          ✦
        </span>
      ))}
    </div>
  )
}

// ============================================
// DATE STAMP
// ============================================
interface DateStampProps {
  date?: Date
  format?: 'short' | 'long'
  className?: string
}

export const DateStamp: React.FC<DateStampProps> = ({
  date = new Date(),
  format = 'short',
  className = ''
}) => {
  const { isDiary } = useTheme()
  if (!isDiary) return null

  const formatDate = (d: Date, fmt: string) => {
    if (fmt === 'short') {
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })
    }
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
  }

  return (
    <span
      className={`inline-block diary-date-stamp ${className}`}
      style={{
        padding: '4px 12px',
        border: '2px solid #5C4033',
        borderRadius: '2px',
        fontFamily: "'Courier New', monospace",
        fontSize: '11px',
        color: '#5C4033',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        transform: 'rotate(-2deg)',
      }}
    >
      {formatDate(date, format)}
    </span>
  )
}

// ============================================
// TAPE CORNERS (for photos/images)
// ============================================
interface TapeCornersProps {
  color?: 'yellow' | 'white' | 'clear'
  className?: string
  children: React.ReactNode
}

export const TapeCorners: React.FC<TapeCornersProps> = ({
  color = 'yellow',
  className = '',
  children
}) => {
  const { isDiary } = useTheme()
  
  if (!isDiary) return <>{children}</>

  const colors = {
    yellow: 'rgba(255, 243, 205, 0.8)',
    white: 'rgba(255, 255, 255, 0.8)',
    clear: 'rgba(255, 255, 255, 0.4)',
  }

  const tapeStyle = {
    position: 'absolute' as const,
    width: '35px',
    height: '18px',
    background: colors[color],
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
  }

  return (
    <div className={`relative inline-block ${className}`}>
      {children}
      {/* Top left */}
      <div style={{ ...tapeStyle, top: '-8px', left: '-8px', transform: 'rotate(-45deg)' }} />
      {/* Top right */}
      <div style={{ ...tapeStyle, top: '-8px', right: '-8px', transform: 'rotate(45deg)' }} />
      {/* Bottom left */}
      <div style={{ ...tapeStyle, bottom: '-8px', left: '-8px', transform: 'rotate(45deg)' }} />
      {/* Bottom right */}
      <div style={{ ...tapeStyle, bottom: '-8px', right: '-8px', transform: 'rotate(-45deg)' }} />
    </div>
  )
}

// ============================================
// DRAWN CHECKBOX
// ============================================
interface DrawnCheckboxProps {
  checked: boolean
  onChange?: (checked: boolean) => void
  label?: string
  className?: string
}

export const DrawnCheckbox: React.FC<DrawnCheckboxProps> = ({
  checked,
  onChange,
  label,
  className = ''
}) => {
  const { isDiary } = useTheme()

  if (!isDiary) {
    return (
      <label className={`flex items-center gap-2 cursor-pointer ${className}`}>
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange?.(e.target.checked)}
          className="w-4 h-4"
        />
        {label && <span>{label}</span>}
      </label>
    )
  }

  return (
    <label className={`flex items-center gap-3 cursor-pointer ${className}`}>
      <div
        onClick={() => onChange?.(!checked)}
        className="relative w-6 h-6 flex items-center justify-center"
        style={{
          border: '2px solid #5C4033',
          borderRadius: '3px',
          background: checked ? '#FDF8F3' : 'transparent',
        }}
      >
        {checked && (
          <svg width="18" height="18" viewBox="0 0 24 24" className="diary-checkmark checked">
            <path
              d="M4 12 L10 18 L20 6"
              fill="none"
              stroke="#5C4033"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                strokeDasharray: 24,
                animation: 'diary-checkmark-draw 0.3s ease forwards',
              }}
            />
          </svg>
        )}
      </div>
      {label && (
        <span
          style={{
            fontFamily: "var(--font-handwritten, 'Kalam', cursive)",
            color: '#2D2A26',
            textDecoration: checked ? 'line-through' : 'none',
            opacity: checked ? 0.6 : 1,
          }}
        >
          {label}
        </span>
      )}
    </label>
  )
}

// ============================================
// PAGE NUMBER
// ============================================
interface PageNumberProps {
  number: number | string
  position?: 'bottom-left' | 'bottom-center' | 'bottom-right'
  className?: string
}

export const PageNumber: React.FC<PageNumberProps> = ({
  number,
  position = 'bottom-center',
  className = ''
}) => {
  const { isDiary } = useTheme()
  if (!isDiary) return null

  const positions = {
    'bottom-left': { bottom: '16px', left: '24px' },
    'bottom-center': { bottom: '16px', left: '50%', transform: 'translateX(-50%)' },
    'bottom-right': { bottom: '16px', right: '24px' },
  }

  return (
    <div
      className={`absolute ${className}`}
      style={{
        ...positions[position],
        fontFamily: "var(--font-body, 'Lora', serif)",
        fontSize: '14px',
        color: '#8B7355',
        fontStyle: 'italic',
      }}
    >
      — {number} —
    </div>
  )
}

export default {
  PaperClip,
  WashiTape,
  CoffeeStain,
  Stamp,
  BookmarkRibbon,
  DoodleStars,
  DateStamp,
  TapeCorners,
  DrawnCheckbox,
  PageNumber,
}
